
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";

import Icon from "@/components/UI/Icon.vue";
import DropdownDragHandle from "@/components/Reusable/Dropdown/DropdownDragHandle.vue";
import { configStore } from "@/store/store";
import { uiStore } from "@/store/store";

@Component({
  components: { DropdownDragHandle, Icon }
})
export default class Dropdown extends Vue {
  @Prop({ default: false })
  value!: boolean;

  @Prop({ default: true })
  closeOnClick!: boolean;

  dropdownOpen = false;

  @Prop()
  title!: string;

  @Prop({ default: 0 })
  nudgeRight!: number;

  @Prop({ default: 0 })
  nudgeTop!: number;

  @Prop({ default: 0 })
  nudgeLeft!: number;

  @Prop({ default: "auto" })
  maxWidth!: number | string;

  @Prop({ default: undefined })
  minWidth!: number | string | undefined;

  @Prop({ default: "100vh" })
  maxHeight!: number | string;

  @Prop()
  contentClass?: string;

  offsetY = 0;
  offsetX = 0;
  observer?: ResizeObserver;

  @Watch("value", { immediate: true })
  listenForModelChanges() {
    if (this.isPreviewMode) return;

    this.$nextTick(() => {
      this.dropdownOpen = this.value;
    });
  }

  @Watch("dropdownOpen")
  listenForInternalChanges() {
    if (!this.dropdownOpen || !this.internalCloseOnClick) {
      return;
    }

    this.$nextTick(() => {
      this.bindListeners();
    });
  }

  beforeDestroy() {
    this.unbindListeners();
  }

  get canvas(): Document | undefined {
    // Optional chaining because canvas document doesn't exist in sub-apps
    return window.Breakdance?.canvas?.document;
  }

  get isPreviewMode() {
    return uiStore.isPreviewMode;
  }

  get internalCloseOnClick() {
    return this.isPreviewMode ? false : this.closeOnClick;
  }

  get internalValue() {
    return this.dropdownOpen;
  }

  set internalValue(value) {
    this.setVisibility(value);
  }

  get builderMode() {
    return configStore.builderMode;
  }

  close() {
    this.setVisibility(false);
  }

  closeOnEsc(event: Event) {
    if (this.isPreviewMode) return;

    if (this.internalValue) {
      this.close();
      event.preventDefault();
    }
  }

  setVisibility(value: boolean) {
    if (this.isPreviewMode) return;

    this.dropdownOpen = value;
    this.$emit("input", value);
  }

  bindListeners() {
    this.canvas?.addEventListener("click", this.close, {
      once: true
    });

    document.addEventListener(
      "close-dropdowns-when-escape-is-pressed",
      this.closeOnEsc
    );

    this.observer = new ResizeObserver(this.recalculatePositionOnHeightChange);
    this.observer.observe(this.$refs.content as HTMLElement);
  }

  unbindListeners() {
    this.canvas?.removeEventListener("click", this.close);

    document.removeEventListener(
      "close-dropdowns-when-escape-is-pressed",
      this.closeOnEsc
    );

    this.observer?.disconnect();
  }

  get internalNudgeTop() {
    return this.nudgeTop + this.offsetY;
  }

  get internalNudgeRight() {
    return this.nudgeRight + this.offsetX;
  }

  recalculatePositionOnHeightChange() {
    const target = this.$refs.content as HTMLElement;
    if (!target) return;

    const menu = target.closest(".v-menu__content") as HTMLElement;

    const rect = menu.getBoundingClientRect();
    const maxY = window.innerHeight - rect.height;

    if (rect.y > maxY) {
      menu.style.top = `${maxY}px`;
    }
  }
}
